import { supabase } from './supabase';
import { DEFAULT_TEMPLATE } from '../theme/templates';
import type { CvCriteria, CvTemplate, GeneratedCv, OutputLanguage } from '../types';

// Sections structurées stockées dans cvs.content / cv_versions.content (jsonb).
function toContent(cv: GeneratedCv) {
  return {
    headline: cv.headline,
    experiences: cv.experiences,
    projects: cv.projects,
    education: cv.education,
    skills: cv.skills,
    languages: cv.languages,
    interests: cv.interests,
  };
}

/**
 * Score à écrire en base : null quand le CV n'a pas été noté (optimisé par modifications). Un faux 0
 * s'afficherait « 0/100 » dans Mes CV et fausserait la moyenne de l'administration.
 */
export function scoreAEnregistrer(cv: GeneratedCv): number | null {
  return cv.score_inconnu ? null : cv.ats_score;
}

/**
 * Persiste un CV généré par l'IA : ligne `cvs` + version initiale `cv_versions`.
 * Le 1er CV de l'utilisateur devient le CV principal. Renvoie l'id du CV créé.
 */
export async function saveGeneratedCv(
  cv: GeneratedCv,
  title: string,
  template: CvTemplate = DEFAULT_TEMPLATE,
  accentColor: string | null = null,
  // Langue de RÉDACTION du document. À ne pas confondre avec `languages`, juste en dessous, qui
  // porte les langues PARLÉES du candidat.
  outputLanguage: OutputLanguage = 'fr',
): Promise<string> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Tu dois être connecté pour enregistrer ton CV.');

  const { count, error: countError } = await supabase
    .from('cvs')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id);
  if (countError) throw countError;

  const content = toContent(cv);

  const { data, error } = await supabase
    .from('cvs')
    .insert({
      user_id: user.id,
      title,
      template,
      accent_color: accentColor,
      ats_score: scoreAEnregistrer(cv),
      content,
      languages: cv.languages,
      output_language: outputLanguage,
      is_primary: (count ?? 0) === 0,
    })
    .select('id')
    .single();
  if (error) throw error;

  const cvId = (data as { id: string }).id;

  const { error: versionError } = await supabase.from('cv_versions').insert({
    cv_id: cvId,
    content,
    ats_score: scoreAEnregistrer(cv),
    note: 'ai_generated',
  });
  if (versionError) throw versionError;

  return cvId;
}

/**
 * Met à jour un CV existant après édition manuelle : écrase `content` + `ats_score` + `languages`.
 * Pas d'historique (on n'utilise pas `cv_versions`). RLS : `user_id = auth.uid()`.
 */
export interface CvSummary {
  id: string;
  title: string;
  content: Record<string, unknown>; // sections jsonb (headline, experiences, …)
}

// Dernière notation persistée (table cv_last_score). `advice` alimente à la fois le compteur
// « N améliorations » de la carte et « Voir le détail », qui ne relance donc aucune analyse.
export interface LastCvScore {
  ats_score: number;
  advice: string[];
  created_at: string;
  // Lot 9b : absents d'une notation antérieure à la migration 0084 (ou d'un serveur qui ne les écrit
  // pas encore). null et undefined veulent dire la même chose : inconnu.
  criteria?: CvCriteria | null;
  cv_title?: string | null;
}

// CV sauvegardé avec toutes les colonnes nécessaires à la liste « Mes CV » et à la réouverture.
export interface SavedCv {
  id: string;
  title: string;
  created_at: string;
  is_primary: boolean;
  template: CvTemplate;
  accent_color: string | null;
  ats_score: number | null;
  content: Record<string, unknown>;
  output_language: OutputLanguage;
}

/**
 * Dernière notation de CV, pour la carte de score de Carrière. `null` = aucune notation à ce jour
 * (l'écran affiche alors 0/100 et invite à en lancer une).
 *
 * La table n'a qu'une policy de LECTURE : la ligne est écrite par claude-cv (service_role), jamais
 * par le client — c'est ce qui rend le score non falsifiable. RLS scope la lecture au propriétaire,
 * donc aucun filtre `user_id` ici.
 */
export async function getLastCvScore(): Promise<LastCvScore | null> {
  const complet = await supabase
    .from('cv_last_score')
    .select('ats_score, advice, created_at, criteria, cv_title')
    .maybeSingle();
  if (!complet.error) return (complet.data as LastCvScore | null) ?? null;
  // Colonnes de la migration 0084 pas encore en base : on relit sans elles plutôt que d'afficher
  // 0/100 à quelqu'un qui a une notation. Si la lecture simple échoue aussi, l'erreur est la vraie.
  const simple = await supabase.from('cv_last_score').select('ats_score, advice, created_at').maybeSingle();
  if (simple.error) throw simple.error;
  return (simple.data as LastCvScore | null) ?? null;
}

// Nombre de CV enregistrés (carte « Mes CV »). `head: true` : on ne rapatrie aucune ligne.
export async function countCvs(): Promise<number> {
  const { count, error } = await supabase.from('cvs').select('id', { count: 'exact', head: true });
  if (error) throw error;
  return count ?? 0;
}

// Liste les CV sauvegardés de l'utilisateur (pour le simulateur d'entretien notamment).
export async function listCvs(): Promise<CvSummary[]> {
  const { data, error } = await supabase
    .from('cvs')
    .select('id, title, content')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as CvSummary[]) ?? [];
}

// Liste enrichie pour l'écran « Mes CV » : titre, date, principal, template + contenu pour rouvrir.
export async function listSavedCvs(): Promise<SavedCv[]> {
  const { data, error } = await supabase
    .from('cvs')
    .select('id, title, created_at, is_primary, template, accent_color, ats_score, content, output_language')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data as SavedCv[]) ?? [];
}

/**
 * Désigne `cvId` comme CV principal : retire `is_primary` de tous les CV de l'utilisateur,
 * puis le pose sur la cible (un seul principal à la fois). RLS scope les écritures à l'utilisateur.
 */
export async function setPrimaryCv(cvId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error('Tu dois être connecté pour modifier tes CV.');

  const { error: clearError } = await supabase
    .from('cvs')
    .update({ is_primary: false })
    .eq('user_id', user.id);
  if (clearError) throw clearError;

  const { error } = await supabase.from('cvs').update({ is_primary: true }).eq('id', cvId);
  if (error) throw error;
}

// Supprime un CV. Les `cv_versions` liées partent en cascade (FK `on delete cascade`, cf. 0001).
export async function deleteCv(cvId: string): Promise<void> {
  const { error } = await supabase.from('cvs').delete().eq('id', cvId);
  if (error) throw error;
}

export async function updateCv(
  cvId: string,
  cv: GeneratedCv,
  template: CvTemplate = DEFAULT_TEMPLATE,
  accentColor: string | null = null,
): Promise<void> {
  const { error } = await supabase
    .from('cvs')
    .update({
      content: toContent(cv),
      ats_score: scoreAEnregistrer(cv),
      languages: cv.languages,
      template,
      accent_color: accentColor,
    })
    .eq('id', cvId);
  if (error) throw error;
}
